import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, AlertOctagon, Circle, X, FileText, Mic, Image as ImageIcon, Video as VideoIcon, Save } from "lucide-react";
import api from "../lib/api";
import { uploadFile, uploadUrl } from "../lib/uploads";
import DateTimePicker from "../components/DateTimePicker";
import SearchableSelect from "../components/SearchableSelect";
import VoiceTextarea from "../components/VoiceTextarea";
import { useAuth } from "../context/AuthContext";

const PRIORITIES = [
  { v: "urgent", label: "Urgent", color: "#F43F5E", icon: AlertOctagon },
  { v: "high", label: "High", color: "#F59E0B", icon: AlertOctagon },
  { v: "normal", label: "Normal", color: "#1FB6C1", icon: Circle },
  { v: "low", label: "Low", color: "#647087", icon: Circle },
];

const empty = {
  title: "",
  description: "",
  assigned_to_ids: [],
  priority: "normal",
  status: "todo",
  due_date: "",
  attachments: [],
  linked_customer_id: "",
  linked_jobcard_id: "",
  recurring: false,
  recurrence_type: "once",
  recurrence_day_of_week: 0,
  recurrence_day_of_month: 1,
  recurrence_date: "",
};

/* Wave 7-fix mobile polish — full-page replacement of the old "New task"
   mini-modal. The form itself is identical; the surface is now a route so
   the user has the entire viewport (especially valuable on phones). */
export default function TaskNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      const [u, c] = await Promise.all([api.get("/users"), api.get("/customers")]);
      setUsers(u.data);
      setCustomers(c.data);
    })();
  }, []);

  const toggleAssignee = (uid) => {
    setForm((f) => ({
      ...f,
      assigned_to_ids: f.assigned_to_ids.includes(uid)
        ? f.assigned_to_ids.filter((x) => x !== uid)
        : [...f.assigned_to_ids, uid],
    }));
  };

  const onAttachUpload = async (file) => {
    if (!file) return;
    const att = await uploadFile(file);
    setForm((f) => ({ ...f, attachments: [...f.attachments, att] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const payload = { ...form };
      if (!payload.assigned_to_ids?.length) payload.assigned_to_ids = [user.id];
      if (!payload.due_date) delete payload.due_date;
      if (!payload.linked_customer_id) delete payload.linked_customer_id;
      if (!payload.linked_jobcard_id) delete payload.linked_jobcard_id;
      if (!payload.recurring) {
        delete payload.recurrence_type;
        delete payload.recurrence_day_of_week;
        delete payload.recurrence_day_of_month;
        delete payload.recurrence_date;
      } else {
        if (payload.recurrence_type !== "weekly") delete payload.recurrence_day_of_week;
        if (payload.recurrence_type !== "monthly") delete payload.recurrence_day_of_month;
        if (payload.recurrence_type !== "once") delete payload.recurrence_date;
        else if (!payload.recurrence_date) delete payload.recurrence_date;
      }
      await api.post("/tasks", payload);
      navigate("/tasks");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not create task");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 max-w-3xl mx-auto" data-testid="task-new-page">
      <div className="flex items-center gap-3">
        <Link to="/tasks" className="hv-btn hv-btn-ghost" data-testid="task-new-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Workboard · new</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">New task</h1>
        </div>
      </div>

      <form onSubmit={submit} className="hv-card p-5 md:p-6 space-y-4" data-testid="task-form">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Title *</label>
          <input
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="hv-input mt-1"
            autoFocus
            data-testid="task-input-title"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
            Instruction <span className="text-[#647087] normal-case">(long-form — shows in the list view)</span>
          </label>
          <VoiceTextarea
            value={form.description}
            onChange={(v) => setForm({ ...form, description: v })}
            rows={5}
            className="mt-1"
            context="lead_activity"
            testIdPrefix="task-desc-voice"
            data-testid="task-input-description"
            placeholder="What to do, where, any context the assignee needs at a glance. Or tap the mic."
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Priority</label>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {PRIORITIES.map((p) => {
              const active = form.priority === p.v;
              return (
                <button
                  type="button"
                  key={p.v}
                  onClick={() => setForm({ ...form, priority: p.v })}
                  className="flex items-center justify-center gap-2 py-2 rounded-md border transition text-xs font-bold uppercase tracking-wider"
                  data-testid={`task-priority-${p.v}`}
                  style={{
                    borderColor: active ? p.color : "rgba(255,255,255,0.1)",
                    background: active ? `${p.color}1a` : "transparent",
                    color: active ? p.color : "#a0aabf",
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
            Assign to (multi-select for groups; default = you)
          </label>
          <div className="flex flex-wrap gap-2 mt-2">
            {users.map((u) => {
              const active = form.assigned_to_ids.includes(u.id);
              return (
                <button
                  type="button"
                  key={u.id}
                  onClick={() => toggleAssignee(u.id)}
                  className="px-3 py-1.5 rounded-full border text-xs"
                  style={{
                    borderColor: active ? "#1FB6C1" : "rgba(255,255,255,0.15)",
                    background: active ? "#1FB6C11a" : "transparent",
                    color: active ? "#1FB6C1" : "#a0aabf",
                  }}
                >
                  {u.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Due date</label>
            <DateTimePicker
              value={form.due_date}
              onChange={(v) => setForm({ ...form, due_date: v })}
              placeholder="Pick due date"
              showTime={false}
              testId="task-input-due"
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Link to customer</label>
            <SearchableSelect
              value={form.linked_customer_id || ""}
              onChange={(id) => setForm({ ...form, linked_customer_id: id })}
              options={customers.map((c) => ({
                id: c.id,
                label: c.name,
                sublabel: [c.external_ref && `Acc# ${c.external_ref}`, c.vat_number && `VAT ${c.vat_number}`, c.phone, c.email].filter(Boolean).join(" · "),
                searchTerms: [c.name, c.external_ref, c.vat_number, c.phone, c.whatsapp, c.email, c.industry, c.physical_address, c.legal_name, c.short_id].filter(Boolean),
              }))}
              placeholder="— None —"
              testId="task-input-customer"
              emptyHint="No customers match. Try VAT, account no., phone or email."
              onCreateNew={(q) => {
                navigate(`/customers/new?name=${encodeURIComponent(q || "")}&from=tasks/new`);
              }}
            />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Attachments</label>
          <AttachmentPicker
            items={form.attachments}
            onAdd={onAttachUpload}
            onRemove={(id) =>
              setForm((f) => ({ ...f, attachments: f.attachments.filter((a) => a.id !== id) }))
            }
          />
        </div>

        {/* Recurrence */}
        <div className="border border-white/10 rounded-md p-3 bg-white/[0.02] space-y-2" data-testid="task-recurrence-editor">
          <div className="flex items-center gap-2">
            <input
              id="task_recurring"
              type="checkbox"
              checked={!!form.recurring}
              onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
              data-testid="task-recurring-toggle"
            />
            <label htmlFor="task_recurring" className="text-xs uppercase tracking-wider text-[#a0aabf]">
              Recurring task
            </label>
          </div>
          {form.recurring && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#647087]">Repeat</label>
                <select
                  value={form.recurrence_type}
                  onChange={(e) => setForm({ ...form, recurrence_type: e.target.value })}
                  className="hv-input mt-1"
                  data-testid="task-recurrence-type"
                >
                  <option value="once">Once</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              {form.recurrence_type === "weekly" && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#647087]">Day of week</label>
                  <select
                    value={form.recurrence_day_of_week}
                    onChange={(e) => setForm({ ...form, recurrence_day_of_week: Number(e.target.value) })}
                    className="hv-input mt-1"
                  >
                    {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                      <option key={i} value={i}>{d}</option>
                    ))}
                  </select>
                </div>
              )}
              {form.recurrence_type === "monthly" && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#647087]">Day of month</label>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={form.recurrence_day_of_month}
                    onChange={(e) => setForm({ ...form, recurrence_day_of_month: Number(e.target.value) })}
                    className="hv-input mt-1"
                  />
                </div>
              )}
              {form.recurrence_type === "once" && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#647087]">Date</label>
                  <DateTimePicker
                    value={form.recurrence_date}
                    onChange={(v) => setForm({ ...form, recurrence_date: v })}
                    showTime={false}
                    placeholder="Pick date"
                    className="mt-1"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {err && <div className="text-sm text-rose-300">{err}</div>}

        <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
          <Link to="/tasks" className="hv-btn hv-btn-ghost">Cancel</Link>
          <button
            type="submit"
            disabled={busy}
            className="hv-btn hv-btn-primary"
            data-testid="task-submit"
          >
            <Save className="w-4 h-4" /> {busy ? "Creating…" : "Create task"}
          </button>
        </div>
      </form>
    </div>
  );
}

function AttachmentPicker({ items, onAdd, onRemove }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      await onAdd(f);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="space-y-2 mt-2">
      <label className="hv-btn hv-btn-ghost cursor-pointer w-fit" data-testid="task-attach-pick">
        <input type="file" hidden onChange={onPick} accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx" />
        <FileText className="w-4 h-4" /> {busy ? "Uploading…" : "Attach file"}
      </label>
      {items?.length > 0 && (
        <ul className="space-y-1.5">
          {items.map((a) => {
            const Icon =
              a.mime?.startsWith("image/") ? ImageIcon :
              a.mime?.startsWith("video/") ? VideoIcon :
              a.mime?.startsWith("audio/") ? Mic : FileText;
            return (
              <li
                key={a.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded border border-white/5 bg-white/[0.02] text-xs"
              >
                <Icon className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
                <a
                  href={uploadUrl(a)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 min-w-0 truncate text-[#dde3ec] hover:text-white"
                >
                  {a.name || "attachment"}
                </a>
                <button
                  type="button"
                  onClick={() => onRemove(a.id)}
                  className="text-[#647087] hover:text-rose-300"
                  aria-label="Remove attachment"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
